import { ComponentProps, PropsWithChildren, isValidElement } from "react";

import { VariantProps, cva } from "class-variance-authority";

import { cn } from "@/common/utils/css.utils";
import { Flavor } from "@/ui/typography/Flavor";

const LabelStyle = cva("flex items-center space-x-1 px-2 py-1 font-title", {
  variants: {
    color: {
      burst: "",
      curious: "",
      brand: "",
      attention: "",
      grey: "",
    },
    variant: {
      light: "",
      dark: "text-white",
    },
    size: {
      small: "px-2 py-0.5 text-sm",
      medium: "px-2 py-0.5 text-base",
      large: "px-2 py-1 text-base",
    },
    corners: {
      ["rounded-full"]: "rounded-full",
      rounded: "rounded-[0.25rem]",
      square: "",
    },
    weight: {
      normal: "",
      bold: "font-bold",
    },
  },
  compoundVariants: [
    {
      color: "burst",
      variant: "light",
      className: "bg-burst-100 text-burst-900",
    },
    {
      color: "curious",
      variant: "light",
      className: "bg-curious-100 text-curious-800",
    },
    {
      color: "brand",
      variant: "light",
      className: "bg-flavour-100 text-brand-600",
    },
    {
      color: "attention",
      variant: "light",
      className: "bg-attention-100 text-attention-900",
    },
    {
      color: "grey",
      variant: "light",
      className: "bg-grey-100 text-grey-800",
    },
    {
      color: "grey",
      variant: "dark",
      className: "bg-grey-700",
    },
    {
      color: "burst",
      variant: "dark",
      className: "bg-burst-500",
    },
    {
      color: "curious",
      variant: "dark",
      className: "bg-curious-500",
    },
    {
      color: "brand",
      variant: "dark",
      className: "bg-brand-500",
    },
    {
      color: "attention",
      variant: "dark",
      className: "bg-attention-500",
    },
  ],
  defaultVariants: {
    color: "curious",
    variant: "light",
    size: "medium",
    corners: "rounded-full",
    weight: "normal",
  },
});

export type InfoLabelProps = {
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
} & Omit<ComponentProps<"span">, "color"> &
  VariantProps<typeof LabelStyle>;

export const InfoLabel = ({
  color,
  variant,
  size,
  corners,
  weight,
  leftIcon,
  rightIcon,
  className,
  children,
  ...props
}: PropsWithChildren<InfoLabelProps>) => {
  const renderChildren = () => {
    /** If children is a primitive - text, for example. */
    if (!isValidElement(children)) {
      return (
        <Flavor
          size={size === "small" ? "small" : "medium"}
          className="overflow-x-hidden text-ellipsis"
        >
          {children}
        </Flavor>
      );
    }

    return children;
  };

  return (
    <span
      className={cn(
        LabelStyle({ color, variant, size, corners, weight }),
        className
      )}
      {...props}
    >
      {leftIcon}
      {renderChildren()}
      {rightIcon}
    </span>
  );
};
